/* Bestanden van een project: de projectmap in SharePoint, gegroepeerd tot
 * documenten met hun versies, plus de omschrijving per versie
 * (project_file_versions, migratie 096).
 *
 * Lezen en schrijven gaan via gv-crm-mcp (routes /app/…, ADR 0017): dezelfde
 * controles als de MCP-tools, met de gv-files-app in plaats van het eigen
 * Microsoft-token van de gebruiker. Een nieuwe versie is altijd een nieuw bestand
 * naast de vorige; overschrijven kan niet.
 *
 * Is gv-crm-mcp (nog) niet bereikbaar, dan komt de lijst uit de bestandscache,
 * zonder uploaden. Zo blijft de pagina bruikbaar tijdens een deploy. */

import {
  FileVersionError,
  groupDocuments,
  nextVersionName,
  parseFileName,
  previousVersionIn,
  type DocumentGroup,
  type ParsedFileName,
} from '../../../../supabase/functions/_shared/fileVersions';
import { personName } from '../../../../supabase/functions/_shared/personName';

export interface BestandenClient {
  from(tabel: string): any;
  functions: { invoke(naam: string, opties: { body: unknown }): Promise<{ data: any; error: any }> };
}

export interface Bestand {
  id: string;
  name: string;
  /** Relatief aan de projectmap; '' is de projectmap zelf. */
  folder: string;
  size: number | null;
  modified: string | null;
  web_url: string | null;
}

export interface Versieregel {
  file_id: string;
  file_name: string;
  message: string;
  source: string;
  client: string | null;
  previous_file_name: string | null;
  created_at: string;
  /** Wie de versie plaatste (project_file_versions.author_id), met naam. */
  author_id: string | null;
  author_name: string | null;
}

export interface Bestanden {
  bron: 'sharepoint' | 'cache';
  map: string | null;
  mappen: string[];
  bestanden: Bestand[];
  documenten: DocumentGroup<Bestand>[];
  /** Omschrijving per bestand (file_id). */
  versies: Map<string, Versieregel>;
  afgekapt: boolean;
  /** Waarom de lijst uit de cache komt. */
  melding: string | null;
}

export class BestandenFout extends Error {
  override name = 'BestandenFout';
  /** 'geweigerd' (een controle), 'drift' (SharePoint gelukt, CRM niet), of 'fout'. */
  soort: string;
  constructor(soort: string, message: string) {
    super(message);
    this.soort = soort;
  }
}

const APP = 'gv-crm-mcp/app';

/** Roept een /app/-route aan en maakt van een HTTP-fout een leesbare melding. */
export async function roepAan<T>(client: BestandenClient, route: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await client.functions.invoke(`${APP}/${route}`, { body });
  if (!error) return data as T;
  let soort = 'fout';
  let melding = error.message ?? String(error);
  const antwoord: Response | undefined = error.context instanceof Response ? error.context : undefined;
  if (antwoord) {
    try {
      const j = await antwoord.clone().json();
      soort = j.error ?? soort;
      melding = j.message ?? melding;
    } catch {
      /* geen JSON: de melding van supabase-js blijft staan */
    }
    if (antwoord.status === 404) soort = 'niet-gevonden';
  }
  throw new BestandenFout(soort, melding);
}

/** Sorteert documenten op het nieuwste bestand, nieuwste eerst. */
export function sorteerDocumenten(docs: DocumentGroup<Bestand>[]): DocumentGroup<Bestand>[] {
  const laatste = (d: DocumentGroup<Bestand>) =>
    d.versions.flatMap((v) => v.files.map((f) => f.file.modified ?? '')).sort().at(-1) ?? '';
  return [...docs].sort((a, b) => (laatste(a) < laatste(b) ? 1 : laatste(a) > laatste(b) ? -1 : a.title.localeCompare(b.title, 'nl')));
}

function bouw(bron: Bestanden['bron'], map: string | null, mappen: string[], bestanden: Bestand[], regels: Versieregel[], afgekapt: boolean, melding: string | null): Bestanden {
  return {
    bron,
    map,
    mappen,
    bestanden,
    documenten: sorteerDocumenten(groupDocuments(bestanden)),
    versies: new Map(regels.map((r) => [r.file_id, r])),
    afgekapt,
    melding,
  };
}

type RuweVersieregel = Omit<Versieregel, 'author_id' | 'author_name' | 'client' | 'previous_file_name'> &
  Partial<Pick<Versieregel, 'author_id' | 'author_name' | 'client' | 'previous_file_name'>>;

/** Zet bij elke versieregel de naam van wie hem plaatste; gv-crm-mcp levert die sinds 098 zelf mee. */
export async function metNamen(client: BestandenClient, regels: RuweVersieregel[]): Promise<Versieregel[]> {
  const zonder = [...new Set(regels.filter((r) => r.author_id && !r.author_name).map((r) => r.author_id as string))];
  const namen = new Map<string, string>();
  if (zonder.length) {
    const { data } = await client.from('profiles').select('id, display_name, email').in('id', zonder);
    for (const p of (data ?? []) as { id: string; display_name: string | null; email: string | null }[]) {
      const n = personName(p);
      if (n) namen.set(p.id, n);
    }
  }
  return regels.map((r) => ({
    file_id: r.file_id,
    file_name: r.file_name,
    message: r.message,
    source: r.source,
    client: r.client ?? null,
    previous_file_name: r.previous_file_name ?? null,
    created_at: r.created_at,
    author_id: r.author_id ?? null,
    author_name: r.author_name ?? (r.author_id ? namen.get(r.author_id) ?? null : null),
  }));
}

/** De map uit de cache relatief maken: daar staat soms het hele pad, soms een deel. */
export function relatieveMap(pad: string | null, projectMap: string | null): string {
  const p = (pad ?? '').replace(/^\/+|\/+$/g, '');
  const basis = (projectMap ?? '').replace(/^\/+|\/+$/g, '');
  if (!p || p === basis) return '';
  if (basis && p.startsWith(`${basis}/`)) return p.slice(basis.length + 1);
  return p;
}

async function uitCache(client: BestandenClient, projectId: string, projectMap: string | null, melding: string): Promise<Bestanden> {
  const { data, error } = await client
    .from('sharepoint_file_cache')
    .select('file_id, name, path, size, modified_date, web_url, is_folder')
    .eq('project_id', projectId)
    .eq('is_folder', false);
  if (error) throw error;
  const bestanden: Bestand[] = (data ?? []).map((r: any) => ({
    id: r.file_id,
    name: r.name,
    folder: relatieveMap(r.path, projectMap),
    size: r.size ?? null,
    modified: r.modified_date ?? null,
    web_url: r.web_url ?? null,
  }));
  const { data: regels } = await client
    .from('project_file_versions')
    .select('file_id, file_name, message, source, client, previous_file_name, created_at, author_id')
    .eq('project_id', projectId);
  const mappen = [...new Set(bestanden.map((b) => b.folder).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'nl'));
  return bouw('cache', projectMap, mappen, bestanden, await metNamen(client, regels ?? []), false, melding);
}

export async function haalBestanden(client: BestandenClient, projectId: string, projectMap: string | null): Promise<Bestanden> {
  try {
    const r = await roepAan<any>(client, 'files', { project: projectId });
    const bestanden: Bestand[] = (r.files ?? []).map((f: any) => ({
      id: f.id,
      name: f.name,
      folder: f.folder ?? '',
      size: f.size ?? null,
      modified: f.modified ?? null,
      web_url: f.web_url ?? null,
    }));
    return bouw('sharepoint', r.folder ?? projectMap, r.folders ?? [], bestanden, await metNamen(client, r.versions ?? []), Boolean(r.truncated), null);
  } catch (e) {
    /* Een weigering (geen map gekoppeld, map buiten het patroon) is het echte
       antwoord; alleen een onbereikbare server valt terug op de cache. */
    if (e instanceof BestandenFout && e.soort === 'geweigerd') throw e;
    return uitCache(client, projectId, projectMap, `Live lijst niet beschikbaar (${(e as Error).message}); dit is de laatst bekende stand. Uploaden kan nu niet.`);
  }
}

/** Is `naam` in `map` een hogere versie van een bestand dat er al staat? */
export function vorigeVersie(bestanden: Bestand[], map: string, naam: string): ParsedFileName | null {
  return previousVersionIn(naam, bestanden.filter((b) => b.folder === map).map((b) => b.name));
}

/** Hoe het formulier de nieuwe versie kiest: een kleine of grote update, of een zelf ingevuld nummer. */
export type VersieStap = 'klein' | 'groot' | 'eigen';

export interface VersieKeuze {
  stap: VersieStap;
  /** Alleen bij 'eigen'. */
  versie?: string;
  /** Alleen GV-conventie; leeg = zoals de vorige versie. */
  concept?: boolean;
}

/**
 * Naam en nummer van de nieuwe versie van `vorigeNaam` (Stan, 02-10-2026): standaard
 * een kleine update (3.1 → 3.1.1), een grote (→ 3.2) als je erom vraagt. Buiten de
 * GV-conventie is er één nummer; dat gaat één omhoog. Gooit een FileVersionError
 * met een leesbare melding.
 */
export function doelVersie(vorigeNaam: string, keuze: VersieKeuze): { naam: string; versie: string } {
  const eigen = keuze.versie?.trim() ?? '';
  if (keuze.stap === 'eigen' && !eigen) throw new FileVersionError('Vul een versienummer in, bijvoorbeeld 4.0.');
  const { name, parsed } = nextVersionName(vorigeNaam, {
    ...(keuze.stap === 'eigen' ? { version: eigen } : { step: keuze.stap === 'groot' ? 'large' as const : 'small' as const }),
    ...(keuze.concept === undefined ? {} : { concept: keuze.concept }),
  });
  return { naam: name, versie: parsed.version };
}

/** De nummers achter "Kleine update" en "Grote update"; groot is null als de naam maar één nummer heeft. */
export function versieStappen(vorigeNaam: string): { klein: string; groot: string | null } | null {
  try {
    const klein = doelVersie(vorigeNaam, { stap: 'klein' }).versie;
    const groot = parseFileName(vorigeNaam)?.scheme === 'gv' ? doelVersie(vorigeNaam, { stap: 'groot' }).versie : null;
    return { klein, groot };
  } catch {
    return null;
  }
}

export interface UploadVerzoek {
  projectId: string;
  map: string;
  bestand: File;
  /** 'upload' = nieuw bestand met zijn eigen naam; 'new_version' = volgende versie van `vorige`. */
  modus: 'upload' | 'new_version';
  vorige?: string;
  omschrijving?: string;
  versie?: string;
  concept?: boolean;
}

export interface UploadUitkomst {
  path: string;
  name: string;
  web_url: string | null;
  version?: string;
  /** De rij in project_file_versions (migratie 096) die gv-crm-mcp voor dit bestand schreef. */
  versieId: string | null;
}

/** begin_upload → PUT naar de upload-URL → finish_upload. */
export async function uploadBestand(client: BestandenClient, v: UploadVerzoek, doeFetch: typeof fetch = fetch): Promise<UploadUitkomst> {
  const begin = await roepAan<any>(client, 'begin_upload', {
    project: v.projectId,
    folder: v.map,
    mode: v.modus,
    ...(v.modus === 'upload' ? { file_name: v.bestand.name } : { previous_file_name: v.vorige }),
    ...(v.omschrijving ? { change_description: v.omschrijving } : {}),
    ...(v.versie ? { version: v.versie } : {}),
    ...(v.concept !== undefined && v.modus === 'new_version' ? { concept: v.concept } : {}),
  });
  if (v.bestand.size > begin.max_bytes) {
    throw new BestandenFout('geweigerd', `Bestand is groter dan ${Math.round(begin.max_bytes / 1024 / 1024)} MB.`);
  }
  const put = await doeFetch(begin.upload_url, { method: 'PUT', headers: begin.headers, body: v.bestand });
  if (!put.ok) throw new BestandenFout('fout', `Uploaden naar de tussenopslag mislukte (HTTP ${put.status}).`);
  const klaar = await roepAan<any>(client, 'finish_upload', { upload_id: begin.upload_id });
  return {
    path: klaar.path,
    name: klaar.name ?? begin.target?.file_name,
    web_url: klaar.web_url ?? null,
    version: klaar.version,
    versieId: klaar.crm?.version_id ?? null,
  };
}
